import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  MdEventAvailable, MdAssessment, MdContactPhone, MdCampaign, MdCheckCircle, MdCancel,
  MdAccessTime, MdGroups, MdClass, MdStar, MdArrowBack, MdClose, MdDoneAll, MdNotificationsActive,
  MdCake, MdWarningAmber, MdEdit, MdSearch,
} from 'react-icons/md';
import api from '../../ui/api';
import useData from '../useData';
import useSounds from '../../hooks/useSounds';
import useToastWithSound from '../../hooks/useToastWithSound';
import { useAuth } from '../auth';
import { useNavigate } from '../navIntent';
import { normalize } from '../search/searchEngine';
import { greeting, greetEmoji } from '../greetings';
import './teacherHome.css';

/**
 * صفوفي — the teacher's home page.
 *
 * Carried over from the V1 teacher app (apps/_archive_v1/teacher_app):
 * the time-of-day greeting, the red "N classes still need attendance"
 * reminder, «الخدمات السريعة» tiles, «شعبي الدراسية» with a tick on the
 * classes already done, and the fast register — every pupil starts
 * present and ONE TAP cycles حاضر → غائب → متأخر, with live counters.
 *
 * Data: one call, api.teacherHome (api/src/routes/teacherHome.js).
 */

const STATUS = {
  present: { label: 'حاضر', icon: MdCheckCircle },
  absent: { label: 'غائب', icon: MdCancel },
  late: { label: 'متأخر', icon: MdAccessTime },
};
const NEXT = { present: 'absent', absent: 'late', late: 'present' };
const WEEKDAY = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];


const initials = (name) => {
  const w = String(name || '').trim().split(/\s+/);
  return w.length >= 2 ? w[0][0] + w[1][0] : (w[0] || '؟')[0];
};
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString('ar-SY-u-nu-latn', { hour: '2-digit', minute: '2-digit' }) : '');
const longDate = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('ar-SY-u-nu-latn', { weekday: 'long', day: 'numeric', month: 'long' });

export default function TeacherHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useData(() => api.teacherHome(), []);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const registerRef = useRef(null);

  const classes = useMemo(() => data?.classes || [], [data]);
  const pending = classes.filter((c) => !c.today.taken);
  const done = classes.length - pending.length;
  const totalStudents = classes.reduce((n, c) => n + (c.student_count || 0), 0);
  const schoolDay = data?.is_school_day;

  const shown = useMemo(() => {
    const nq = normalize(q);
    return classes.filter((c) => {
      if (filter === 'pending' && c.today.taken) return false;
      if (filter === 'done' && !c.today.taken) return false;
      if (filter === 'homeroom' && !c.is_homeroom) return false;
      return !nq || normalize(`${c.section_label} ${c.subjects.join(' ')}`).includes(nq);
    });
  }, [classes, filter, q]);

  const openRegister = (id) => {
    setOpenId(id);
    requestAnimationFrame(() => registerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  const openClass = classes.find((c) => c.id === openId) || null;
  const nextPending = pending.find((c) => c.id !== openId) || null;

  if (loading && !data) {
    return (
      <div className="th">
        <div className="th-hero th-skel-hero" />
        <div className="th-skel-row">{[0, 1, 2, 3].map((k) => <div key={k} className="th-skel" />)}</div>
      </div>
    );
  }
  if (error && !data) {
    return <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>;
  }

  const firstName = (data?.teacher?.name || user?.name || '').split(' ')[0];

  return (
    <div className="th">
      {/* ---------- Hero ---------- */}
      <header className="th-hero">
        <div className="th-particles" aria-hidden="true">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} style={{ '--i': i }} />)}
        </div>
        <div className="th-hero-main">
          <div className="th-hero-avatar">{initials(data?.teacher?.name || user?.name)}</div>
          <div>
            <p className="th-greet">{greeting()} {greetEmoji()}</p>
            <h1>أهلاً أ. {firstName} 👋</h1>
            <p className="th-date">{data ? longDate(data.date) : ''}</p>
          </div>
        </div>
        <div className="th-hero-stats">
          <div><b>{classes.length}</b><span>شعبة</span></div>
          <div><b>{totalStudents}</b><span>طالب</span></div>
          <div className={pending.length && schoolDay ? 'is-warn' : 'is-ok'}>
            <b>{done}/{classes.length}</b><span>تفقّد اليوم</span>
          </div>
        </div>
      </header>

      {/* ---------- Reminder (V1's red banner) ---------- */}
      {schoolDay && pending.length > 0 && (
        <button type="button" className="th-remind" onClick={() => openRegister(pending[0].id)}>
          <span className="th-remind-bell"><MdNotificationsActive size={24} /></span>
          <span className="th-remind-text">
            <strong>تذكير: التفقّد</strong>
            <span>
              {pending.length === 1
                ? 'لديك شعبة واحدة لم يتم تسجيل الحضور لها اليوم'
                : `لديك ${pending.length} شعب لم يتم تسجيل الحضور لها اليوم`}
            </span>
          </span>
          <span className="th-remind-go">ابدأ الآن <MdArrowBack size={18} /></span>
        </button>
      )}
      {schoolDay && classes.length > 0 && pending.length === 0 && (
        <div className="th-allset"><MdDoneAll size={22} /> أحسنت! تم تسجيل الحضور لكل شعبك اليوم ✨</div>
      )}
      {data && !schoolDay && (
        <div className="th-weekend">🌿 عطلة نهاية الأسبوع — نتمنى لك راحة طيبة</div>
      )}

      {/* ---------- Quick services ---------- */}
      <section>
        <h2 className="th-h2">الخدمات السريعة</h2>
        <div className="th-tiles">
          <button
            type="button"
            className="th-tile th-tile-att"
            onClick={() => (pending[0] && schoolDay ? openRegister(pending[0].id) : navigate('attendance'))}
          >
            <MdEventAvailable size={30} /><span>تسجيل الحضور</span>
            {schoolDay && pending.length > 0 && <em>{pending.length}</em>}
          </button>
          <button type="button" className="th-tile th-tile-exam" onClick={() => navigate('exams')}>
            <MdAssessment size={30} /><span>الامتحانات</span>
            {data?.exams?.length > 0 && <em>{data.exams.length}</em>}
          </button>
          <button type="button" className="th-tile th-tile-students" onClick={() => navigate('student-directory')}>
            <MdContactPhone size={30} /><span>الطلاب</span>
          </button>
          <button type="button" className="th-tile th-tile-notify" onClick={() => navigate('notifications')}>
            <MdCampaign size={30} /><span>إرسال إشعار</span>
          </button>
        </div>
      </section>

      {/* ---------- The register, opened in place ---------- */}
      {openClass && (
        <div ref={registerRef} className="th-reg-anchor">
          <Register
            key={openClass.id}
            cls={openClass}
            date={data.date}
            schoolDay={schoolDay}
            next={nextPending}
            onClose={() => setOpenId(null)}
            onNext={(id) => openRegister(id)}
            onSaved={reload}
            onEdit={() => navigate('attendance', { sectionId: openClass.id })}
          />
        </div>
      )}

      <div className="th-grid">
        {/* ---------- My classes ---------- */}
        <section className="th-classes">
          <div className="th-classes-head">
            <h2 className="th-h2">شعبي الدراسية</h2>
            {classes.length > 8 && (
              <label className="th-search">
                <MdSearch size={17} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن شعبة أو مادة…" />
              </label>
            )}
          </div>
          <div className="th-filters">
            {[
              ['all', 'الكل', classes.length],
              ['pending', 'لم يُتفقّد', pending.length],
              ['done', 'تم التفقّد', done],
              ...(classes.some((c) => c.is_homeroom) ? [['homeroom', 'صفّي', classes.filter((c) => c.is_homeroom).length]] : []),
            ].map(([id, label, n]) => (
              <button key={id} type="button" className={`th-filter${filter === id ? ' is-active' : ''}`} onClick={() => setFilter(id)}>
                {label}<span>{n}</span>
              </button>
            ))}
          </div>

          {classes.length === 0 ? (
            <div className="th-empty"><MdClass size={40} /><p>لا توجد شعب مسندة إليك حالياً.</p></div>
          ) : shown.length === 0 ? (
            <div className="th-empty"><p>لا توجد شعب مطابقة.</p></div>
          ) : (
            <div className="th-cards" data-guide="th-classes">
              {shown.map((c) => (
                <ClassCard
                  key={c.id}
                  c={c}
                  active={c.id === openId}
                  schoolDay={schoolDay}
                  onRegister={() => openRegister(c.id)}
                  onStudents={() => navigate('student-directory', { sectionId: c.id })}
                />
              ))}
            </div>
          )}
        </section>

        {/* ---------- Side ---------- */}
        <aside className="th-side">
          <section className="th-card">
            <h3><MdWarningAmber size={19} /> طلاب بحاجة إلى متابعة</h3>
            {data.at_risk.length === 0 ? (
              <p className="th-muted">لا يوجد طلاب بغياب متكرر خلال الشهر الأخير 👍</p>
            ) : (
              <ul className="th-list">
                {data.at_risk.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => navigate('student-directory', { studentId: s.id, tab: 'attendance' })}>
                      <span className="th-list-av is-danger">{initials(s.name)}</span>
                      <span className="th-list-main"><strong>{s.name}</strong><small>{s.section_label}</small></span>
                      <span className="th-list-num"><b>{s.absent}</b><small>غياب</small></span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="th-card">
            <h3><MdAssessment size={19} /> امتحانات لم تُنشر بعد</h3>
            {data.exams.length === 0 ? (
              <p className="th-muted">كل امتحاناتك منشورة ✓</p>
            ) : (
              <ul className="th-list">
                {data.exams.map((e) => {
                  const pct = e.student_count ? Math.min(100, Math.round((e.results / e.student_count) * 100)) : 0;
                  return (
                    <li key={e.id}>
                      <button type="button" onClick={() => navigate('exams')}>
                        <span className="th-list-av is-violet"><MdAssessment size={17} /></span>
                        <span className="th-list-main">
                          <strong>{e.title} — {e.subject}</strong>
                          <small>{e.section_label} · {e.date}</small>
                          <span className="th-progress"><span style={{ width: `${pct}%` }} /></span>
                        </span>
                        <span className="th-list-num"><b>{e.results}/{e.student_count}</b><small>علامة</small></span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {data.birthdays.length > 0 && (
            <section className="th-card th-bday">
              <h3><MdCake size={19} /> أعياد ميلاد اليوم 🎉</h3>
              <ul className="th-list">
                {data.birthdays.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => navigate('student-directory', { studentId: s.id })}>
                      <span className="th-list-av is-pink">🎂</span>
                      <span className="th-list-main"><strong>{s.name}</strong><small>{s.section_label}</small></span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function ClassCard({ c, active, schoolDay, onRegister, onStudents }) {
  const t = c.today;
  return (
    <article className={`th-class${t.taken ? ' is-done' : ''}${active ? ' is-open' : ''}`} style={{ '--hue': (c.grade_order * 37) % 360 }}>
      <div className="th-class-top">
        <span className="th-class-grade">{c.grade_title}</span>
        {c.is_homeroom ? <span className="th-badge"><MdStar size={12} /> معلم الصف</span> : null}
        <span className={`th-state${t.taken ? ' is-done' : ''}`}>
          {t.taken ? <><MdCheckCircle size={14} /> تم التفقّد</> : schoolDay ? <><MdAccessTime size={14} /> لم يُتفقّد</> : 'عطلة'}
        </span>
      </div>
      <h3 className="th-class-name">{c.section_label}</h3>
      <div className="th-chips">
        {c.subjects.map((s) => <span key={s}>{s}</span>)}
      </div>

      <div className="th-class-stats">
        <span><MdGroups size={16} /> {c.student_count} طالب</span>
        {t.taken ? (
          <span className="th-today">
            <i className="is-danger">غياب {t.absent}</i><i className="is-warning">تأخير {t.late}</i>
          </span>
        ) : null}
        {c.rate_30 != null && <span title="نسبة الحضور خلال آخر 30 يوماً">حضور {c.rate_30}%</span>}
      </div>

      <div className="th-week" aria-label="آخر خمسة أيام دراسية">
        {c.week.map((w) => {
          const tone = !w.taken ? 'none' : w.absent > 0 ? 'some' : 'full';
          const wd = WEEKDAY[new Date(`${w.date}T00:00:00Z`).getUTCDay()];
          return (
            <span
              key={w.date}
              className={`th-day is-${tone}`}
              title={`${wd} ${w.date}${w.taken ? ` — غياب ${w.absent}، تأخير ${w.late}` : ' — لم يُتفقّد'}`}
            >
              <i />{wd.replace('ال', '').slice(0, 3)}
            </span>
          );
        })}
      </div>

      <div className="th-class-actions">
        <button type="button" className={`th-btn${t.taken || !schoolDay ? ' is-soft' : ''}`} onClick={onRegister} data-guide="th-take">
          <MdEventAvailable size={17} /> {t.taken ? 'عرض التفقّد' : 'أخذ التفقّد'}
        </button>
        <button type="button" className="th-btn is-ghost" onClick={onStudents}>
          <MdGroups size={17} /> الطلاب
        </button>
      </div>
    </article>
  );
}

/**
 * The fast register. Everyone starts حاضر; one tap cycles
 * حاضر → غائب → متأخر (V1's rule). Saving sends only the exceptions,
 * which is all the API stores. Offline, the save waits in the outbox
 * and goes out when the connection returns (ui/api.js).
 */
function Register({ cls, date, schoolDay, next, onClose, onNext, onSaved, onEdit }) {
  const sounds = useSounds();
  const toast = useToastWithSound();
  const { data, loading, error } = useData(() => api.sectionRoster(cls.id, date), [cls.id, date]);
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const taken = data?.taken || saved;

  useEffect(() => {
    if (!data) return;
    const m = {};
    data.students.forEach((s) => { m[s.student_id] = s.status || 'present'; });
    setMarks(m);
  }, [data]);

  const students = data?.students || [];
  const counts = Object.values(marks).reduce((a, s) => ({ ...a, [s]: (a[s] || 0) + 1 }), { present: 0, absent: 0, late: 0 });

  const tap = (id) => {
    if (taken || saving) return;
    sounds.click();
    setMarks((m) => ({ ...m, [id]: NEXT[m[id] || 'present'] }));
  };
  const allPresent = () => {
    const m = {};
    students.forEach((s) => { m[s.student_id] = 'present'; });
    setMarks(m);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.takeAttendance({
        section_id: cls.id,
        date,
        absent_student_ids: students.filter((s) => marks[s.student_id] === 'absent').map((s) => s.student_id),
        late_student_ids: students.filter((s) => marks[s.student_id] === 'late').map((s) => s.student_id),
      });
      setSaved(true);
      toast.success(`تم حفظ تفقّد ${cls.section_label}`);
      onSaved();
    } catch (err) {
      toast.error(err.code === 'ALREADY_EXISTS' ? 'تم أخذ تفقّد هذه الشعبة اليوم مسبقاً' : err.message);
      if (err.code === 'ALREADY_EXISTS') onSaved();
    } finally { setSaving(false); }
  };

  return (
    <section className={`th-reg${taken ? ' is-taken' : ''}`}>
      <div className="th-reg-head">
        <div>
          <span className="th-reg-kicker">{taken ? 'تفقّد اليوم' : 'تسجيل الحضور'}</span>
          <h2>{cls.section_label}</h2>
          <p>{longDate(date)}{cls.today.taken && cls.today.by ? ` · ${cls.today.by} — ${clock(cls.today.at)}` : ''}</p>
        </div>
        <button type="button" className="th-reg-close" onClick={onClose} aria-label="إغلاق"><MdClose size={22} /></button>
      </div>

      <div className="th-counters">
        {['present', 'absent', 'late'].map((k) => {
          const I = STATUS[k].icon;
          return (
            <div key={k} className={`th-counter is-${k}`}>
              <I size={20} /><b>{counts[k] || 0}</b><span>{STATUS[k].label}</span>
            </div>
          );
        })}
      </div>

      {loading ? (
        <div className="th-reg-grid">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="th-skel th-skel-pupil" />)}</div>
      ) : error ? (
        <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>
      ) : saved ? (
        <div className="th-reg-done">
          <div className="th-check"><MdDoneAll size={44} /></div>
          <h3>تم حفظ التفقّد بنجاح</h3>
          <p>حاضر {counts.present} · غائب {counts.absent} · متأخر {counts.late}</p>
          <div className="th-reg-bar">
            {next ? (
              <button type="button" className="th-btn" onClick={() => onNext(next.id)}>
                الشعبة التالية: {next.section_label} <MdArrowBack size={17} />
              </button>
            ) : null}
            <button type="button" className="th-btn is-ghost" onClick={onClose}>إغلاق</button>
          </div>
        </div>
      ) : (
        <>
          {!taken && schoolDay && <p className="th-reg-hint">اضغط على الطالب لتغيير حالته: حاضر ← غائب ← متأخر</p>}
          <div className="th-reg-grid" data-guide="th-reg-grid">
            {students.map((s) => {
              const st = marks[s.student_id] || 'present';
              const I = STATUS[st].icon;
              return (
                <button
                  key={s.student_id}
                  type="button"
                  className={`th-pupil is-${st}`}
                  onClick={() => tap(s.student_id)}
                  disabled={taken || !schoolDay}
                  aria-label={`${s.name}: ${STATUS[st].label}`}
                >
                  <span className="th-pupil-av">{initials(s.name)}</span>
                  <span className="th-pupil-name">{s.name}</span>
                  <span className="th-pupil-st"><I size={15} /> {STATUS[st].label}</span>
                </button>
              );
            })}
          </div>

          <div className="th-reg-bar">
            {taken ? (
              <button type="button" className="th-btn is-soft" onClick={onEdit}><MdEdit size={17} /> تعديل حالة طالب</button>
            ) : schoolDay ? (
              <>
                <button type="button" className="th-btn is-ghost" onClick={allPresent} disabled={saving}>الكل حاضر</button>
                <button type="button" className="th-btn is-save" onClick={save} disabled={saving || !students.length} data-guide="th-reg-save">
                  {saving ? <span className="ui-spinner-sm" /> : <MdDoneAll size={18} />} حفظ التفقّد ({students.length})
                </button>
              </>
            ) : (
              <span className="th-muted">لا يوجد دوام اليوم.</span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
